<template>
  <nav class="flex flex-col gap-4" aria-label="Scan the World social links">
    <ul class="flex flex-wrap items-center gap-4">
      <li v-for="item in socialLinks" :key="item.id">
        <a
          v-if="item.external && item.href"
          :href="item.href"
          :title="item.label"
          :class="['focusable-button', sizeClasses.button]"
          target="_blank"
          rel="noopener noreferrer"
        >
          <span class="sr-only">{{ item.label }}</span>
          <i :class="['pi', item.icon, sizeClasses.icon]" />
        </a>
        <RouterLink
          v-else-if="item.href"
          :to="item.href"
          :title="item.label"
          :class="['focusable-button', sizeClasses.button]"
        >
          <span class="sr-only">{{ item.label }}</span>
          <i :class="['pi', item.icon, sizeClasses.icon]" />
        </RouterLink>
        <span
          v-else
          :title="item.label"
          :class="[
            'focusable-button pointer-events-none opacity-40',
            sizeClasses.button,
          ]"
          aria-disabled="true"
        >
          <span class="sr-only">{{ item.label }}</span>
          <i :class="['pi', item.icon, sizeClasses.icon]" />
        </span>
      </li>
    </ul>
  </nav>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { RouterLink } from 'vue-router';

type LinkSize = 'lg' | 'md' | 'sm';

type SocialLink = {
  id: string;
  label: string;
  icon: string;
  href?: string;
  external?: boolean;
};

const props = withDefaults(
  defineProps<{
    size?: LinkSize;
  }>(),
  {
    size: 'md',
  }
);

const sizeMap: Record<LinkSize, { button: string; icon: string }> = {
  lg: { button: 'h-16 w-16 text-3xl', icon: 'text-3xl' },
  md: { button: 'h-12 w-12 text-2xl', icon: 'text-2xl' },
  sm: { button: 'h-8 w-8 text-lg', icon: 'text-lg' },
};

const sizeClasses = computed(() => sizeMap[props.size] ?? sizeMap.md);

const socialLinks: SocialLink[] = [
  {
    id: 'discord',
    label: 'Join the Scan the World Discord Server!',
    icon: 'pi-discord',
    href: 'https://discord.gg/k7FeGtKk',
    external: true,
  },
  {
    id: 'youtube',
    label: 'Scan the World on YouTube',
    icon: 'pi-youtube',
    href: 'https://www.youtube.com/@scan-the-world',
    external: true,
  },
  {
    id: 'instagram',
    label: 'Scan the World on Instagram',
    icon: 'pi-instagram',
    href: 'https://www.instagram.com/scan_theworld/',
    external: true,
  },
  {
    id: 'twitter',
    label: 'Scan the World on X (coming soon)',
    icon: 'pi-twitter',
  },
  {
    id: 'facebook',
    label: 'Scan the World on Facebook',
    icon: 'pi-facebook',
    href: 'https://www.facebook.com/people/Scan-the-World/100083621680404/',
    external: true,
  },
  {
    id: 'email',
    label: 'Contact Scan the World',
    icon: 'pi-envelope',
    href: '/contact',
  },
];
</script>

<style scoped lang="scss">
.focusable-button {
  @apply bg-surface-50/60 text-surface-950/80 hover:bg-primary/20 hover:text-primary focus-visible:outline-primary/70 flex items-center justify-center rounded-2xl ring-1 transition-colors duration-200 ease-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2;
  --tw-ring-color: var(--stw-glass-border);
}

.sr-only {
  @apply absolute -m-px h-px w-px overflow-hidden whitespace-nowrap border-0 p-0;
}
</style>
